import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { gallery } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-800 shadow-lg transition-colors duration-150 ease-out hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-300 md:h-12 md:w-12';
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const current = gallery[index];
  const goTo = (next: number) => setIndex((next + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  return <section className="w-full bg-teal-900 py-14 text-white md:py-20" data-id="element-63" data-mp="rbrrmxj9uf5a">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-64" data-mp="wb4c5bmo7a4p">
        <header className="mx-auto max-w-4xl text-center" data-id="element-65" data-mp="w9jlvy7e2dcn">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-200" data-id="element-66" data-mp="113fgqrscvk3l">
            Découvrez les fiches de l’intérieur
          </p>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight md:text-5xl" data-id="element-67" data-mp="155shf1s8wrti">
            Voici à quoi ressemble chaque région
          </h2>
        </header>

        <div role="group" aria-roledescription="carrousel" aria-label="Galerie de fiches visuelles" tabIndex={0} onKeyDown={handleKeyDown} className="relative mt-12 rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-300" data-id="element-68" data-mp="dd1trfomobfv">
          <div className="overflow-hidden rounded-xl" data-id="element-69" data-mp="1xsn0bu9ezj4v">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-70" data-mp="1xrbuzq0bm3bn">
              {gallery.map((item, itemIndex) => <figure key={item.title} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-71" data-mp="3b2xj0xphv3j4" data-mpi={itemIndex}>
                  <img src={item.image} alt={`Fiche visuelle ${item.title} : électrodes, canaux et repères anatomiques`} className="pointer-events-none w-full rounded-xl bg-white" draggable={false} loading={itemIndex === 0 ? 'eager' : 'lazy'} data-id="element-72" data-mp="3kv3b07fqxkyb" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Fiche précédente" className={`${arrowClass} left-2 md:-left-5`} data-id="element-73" data-mp="12wlxkdxeytag">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-74" data-mp="ty0bw9wnay5g" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Fiche suivante" className={`${arrowClass} right-2 md:-right-5`} data-id="element-75" data-mp="xxsnkvhc5kf1">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-76" data-mp="1qnhkoxqxd0bd" />
          </button>
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center" aria-live="polite" data-id="element-77" data-mp="1cmezsobo1w21">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-78" data-mp="1hm1mdoddwoni">{current.title}</p>
          <p className="mt-2 text-teal-100" data-id="element-79" data-mp="17rzkyosj7ys">{current.caption}</p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-80" data-mp="67ect84mco7l">
          {gallery.map((item, itemIndex) => <button key={item.title} type="button" onClick={() => goTo(itemIndex)} aria-label={`Voir ${item.title}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-300 ${itemIndex === index ? 'w-8 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} data-id="element-81" data-mp="lhks6ew1a197" data-mpi={itemIndex} />)}
        </div>

        <p className="mt-6 text-center text-sm text-teal-200" data-id="element-82" data-mp="zhlqbmkwyukz">
          Faites défiler ou utilisez les flèches — {index + 1} fiche parmi {total} exemples
        </p>

        <div className="mt-8 flex flex-col items-center text-center" data-id="element-83" data-mp="qf2tbj3t7c6n">
          <CtaButton variant="secondary" size="md" data-id="element-84" data-mp="3g56hrilrifzh">
            Je veux accéder aux 50 Fiches
          </CtaButton>
          <p className="mt-3 text-sm text-teal-100" data-id="element-85" data-mp="3b63jch0af80h">
            Accès immédiat · PDF imprimable · 4 bonus inclus
          </p>
        </div>
      </div>
    </section>;
}